import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MILL_SECONDS } from 'common/constant/general';
import { ALIGN, POSITION } from 'components/analyze/analyze-common';
import { MB_RESPONSE, MB_TYPE, MessageBoxComponent, MessageBoxOptions } from 'components/message-box/message-box.component';
import { SourceSelectionComponent } from 'components/source-selection/source-selection.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { DefinitionService } from 'services/definition/definition.service';
import {
  TelemetryDeliveryMethod,
  TelemetrySchemaType,
  TelemetryType
} from "services/definition/enum";
import {
  ISourceNode,
  ITelemetryNode
} from "services/definition/interface";
import { SpinnerTypes } from 'services/loading/spinner/spinner-type';
import { Logger } from 'src/app/common/utility/logger/logger';

// Loading Source List const
const MAX_TIMEOUT = 60;

// Selection ranges
const MAX_FUNC_TIMEFRAME = 999;
const MIN_FUNC_TIMEFRAME = 1;

// Precision Selection
export const DEFAULT_PRECISION = 1 // 1 Decimal place
const MIN_PRECISION = 0
const MAX_PRECISION = 6

// Defaults
export const DEFAULT_TIMEFRAME = 5 // 5 Seconds

export enum FUNC_TYPE {
  REALTIME = "realtime",
  AVERAGE = "average",
  ENUM = "enum"
}

const LIST_SIGNAL_DEFAULT_SETTINGS: ListTelemetrySettings = {
  order: -1,
  function: FUNC_TYPE.REALTIME,
  functionTimeframe: DEFAULT_TIMEFRAME,
  showSourceName: false,
  showDeviceName: false,
  showUoMSymbol: true,
  showCustomName: false,
  customName: "",
  precision: DEFAULT_PRECISION
}

// LineSettings contained within TelemetryNode of sourceNodes tree
export interface ListTelemetrySettings {
  order: number
  function: FUNC_TYPE
  functionTimeframe: number
  showSourceName: boolean
  showDeviceName: boolean
  showUoMSymbol: boolean
  showCustomName: boolean
  customName: string
  precision: number
}

// Defaults are used by Dashboard for defaults in Chart Widget
const LIST_DEFAULT_SETTINGS: ListSettings = {
  title: {
    showTitle: false,
    text: "",
    position: POSITION.Top,
    align: ALIGN.Start,
    font: {
      weight: 'bolder',
      size: 14
    }
  },
  precision: DEFAULT_PRECISION,
  sourceNodes: [] // Settings are contained within sourceNodes
} // LIST_DEFAULT_SETTINGS

export interface ListSettings {
  title: {
    showTitle: boolean
    text: string
    position: POSITION
    align: ALIGN
    font: {
      weight: string
      size: number
    }
  },
  precision: number,
  sourceNodes: Array<any> | undefined // sourceNodes contain sort
}

@Component({
  selector: 'list-widget-settings',
  templateUrl: './list-widget-settings.component.html',
  styleUrls: ['./list-widget-settings.component.scss']
})
export class ListWidgetSettingsComponent implements OnInit {
  public get minFunctionTimeframe(): number { return MIN_FUNC_TIMEFRAME };
  public get maxFunctionTimeframe(): number { return MAX_FUNC_TIMEFRAME };
  public get minPrecision(): number { return MIN_PRECISION };
  public get maxPrecision(): number { return MAX_PRECISION };
  public fullTree: Array<ISourceNode> = [];

  // Form Control settings
  public userForm!: FormGroup;
  public functionFrmCtl = new FormControl();
  public functionTimeframeFrmCtrl = new FormControl();
  public showSourceNameFrmCtrl = new FormControl();
  public showDeviceNameFrmCtrl = new FormControl();
  public showUoMSymbolFrmCtrl = new FormControl();
  public showCustomNameFrmCtrl = new FormControl();
  public customNameFrmCtrl = new FormControl()
  public showSettingsDrawer: boolean = false;
  public titleShowFrmCtrl = new FormControl();
  public titleTextFrmCtrl = new FormControl();
  public titlePositionFrmCtrl = new FormControl();
  public titleSizeFrmCtrl = new FormControl();
  public precisionFrmCtrl = new FormControl()

  // Used with source-selection
  private showAll: boolean = false;
  private editMode: boolean = true;
  public searchStr: string = '';
  public editingNode!: ITelemetryNode;

  // Spinner
  private loadingId = 0;
  private loadingTimeout = MAX_TIMEOUT;

  constructor(
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<ListWidgetSettingsComponent>,
    public timeoutDialogRef: MatDialogRef<MessageBoxComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { ndx: any, settings: ListSettings }
  ) { }


  public ngOnInit() {

    // If Sources List is not ready, then wait
    if (!DefinitionService.SourcesListReady) {
      this.spinner.show(this.spinnerName);

      this.loadingId = window.setInterval(() => {
        // Did we timeout?
        if (this.loadingTimeout && !--this.loadingTimeout) {
          Logger.error("Source Selection List Timed Out")
          // Tell the user we timed out
          const msgBoxSettings: MessageBoxOptions = {
            type: MB_TYPE.YES_NO,
            title: undefined,
            message: "widgets.common.source-list-timeout"
          }
          const msgBox = this.dialog.open(MessageBoxComponent, {
            data: msgBoxSettings
          });
          msgBox.afterClosed().subscribe((res) => {
            if (res.event === MB_RESPONSE.YES) {
              this.loadingTimeout = MAX_TIMEOUT;
            } else {
              this.spinner.hide(this.spinnerName);
              window.clearInterval(this.loadingId)
              this.loadingId = 0;
              // Close dialog
              this.timeoutDialogRef.close({
                event: 'cancel',
                data: undefined
              });
            }
          }); // afterClosed subscribe
        } // If loadingTimeout

        // See if the Source List is Ready yet
        if (DefinitionService.SourcesListReady) {
          this.spinner.hide(this.spinnerName)
          window.clearInterval(this.loadingId);
          this.loadingId = 0;
          this.fullTree = DefinitionService.getSourcesList();
        }
      }, MILL_SECONDS)
    } else { // Source list is Ready
      // Get my sourceNodes
      this.fullTree = DefinitionService.getSourcesList();
    }

    // If data settings have not been initialized
    if (this.data.settings === undefined) {
      this.data.settings = LIST_DEFAULT_SETTINGS;
    }

    // Setup the userForm for the dialog
    this.userForm = this.formBuilder.group({
      titleShowFrmCtrl: this.data.settings.title.showTitle,
      titleTextFrmCtrl: this.data.settings.title.text,
      titleAlignFrmCtrl: this.data.settings.title.align,
      titleSizeFrmCtrl: this.data.settings.title.font.size
    });

    // If nothing has been configured show the tree
    if ((this.data.settings.sourceNodes == undefined) || !this.data.settings.sourceNodes.length) {
      this.showAll = true;
      this.editMode = !this.showAll;
    }
  } // ngOnInit()

  public get sourcesAvailable(): boolean {
    return this.fullTree.length > 0;
  } // sourcesAvailable

  public isEnum(): boolean {
    if (!this.editingNode)
      return false;
    return (this.editingNode.ltmiRef.schemaType == TelemetrySchemaType.Enum)
  } // isEnum()

  public listSettingsLabels(str: string): string {
    return 'widgets.list-widget.' + str;
  } // listSettingsLabels()

  public widgetCommonLabels(str: string): string {
    return 'widgets.common.' + str;
  } // listSettingsLabels()

  // Handle Keyup
  public keyupEvent(event: { key: string; }) {
    // If Escape Key then cancel search
    if (event.key === "Escape") {
      this.searchStr = "";
    }
  } // cancelSearch()

  public getSearchString(): string {
    return this.searchStr;
  } // getSearchString

  // Show/Hide in Tree View
  public toggleShowAll() {
    // When not showing all, allow editMode for selected
    this.showAll = !this.showAll;
    this.editMode = !this.showAll;

    // Make sure defaults are set
    if (this.editMode) {
      this.data.settings.sourceNodes = <Array<any>>SourceSelectionComponent.pruneTree(this.fullTree)
      this.setTelemetryNodeDefaults(this.data.settings.sourceNodes)
    }
  } // toggleShowAll()

  // Returns an array of TelemetryNodes
  private setTelemetryNodeDefaults(nodes: Array<any>) {
    this.setNodeDefaults(nodes);

    // Determine if any of the needs need order numbers
    this.isNodesOrderRequired(nodes);
    if (this.needsNumbers) {
      this.setNodeOrder(nodes)
    }
  } // setTelemetryNodeDefaults()

  private highest!: number;
  private setNodeOrder(nodes: any): void {
    // Set the node order
    nodes.forEach((node: any) => {
      if (node.children) {
        this.setNodeOrder(node.children);
      } else {
        if (node.settings.order < 0) {
          node.settings.order = ++this.highestOrderNumber;
        }
      }
    });
  } // setNodeOrder

  // Find the highest number
  needsNumbers: boolean = false;
  highestOrderNumber: number = -1;
  private isNodesOrderRequired(nodes: any): void {
    nodes.forEach((node: any) => {
      if (node.children) {
        this.isNodesOrderRequired(node.children);
      } else {
        if (node.settings.order < 0) {
          this.needsNumbers = true;
        }
        if (this.highestOrderNumber < node.settings.order) {
          this.highestOrderNumber = node.settings.order;
        }
      }
    });
  } // isNodesOrderRequired()


  // Set Telemetry Line Defaults
  private setNodeDefaults(nodes: Array<any>): void {
    // Check for new Telemetry Nodes, default nodes
    nodes.forEach((node) => {
      // Is TelemetryNode?
      if (node.children) {
        this.setNodeDefaults(node.children)
      } else { // Telemetry Node
        if (node.settings == undefined) {
          node.settings = structuredClone(LIST_SIGNAL_DEFAULT_SETTINGS);
          // If Enum, then default to Enum
          if (node.ltmiRef.schemaType == TelemetrySchemaType.Enum) {
            node.settings.function = FUNC_TYPE.ENUM
          }
          node.settings.precision = node.ltmiRef?.defaults?.precision != undefined ? Number(node.ltmiRef.defaults.precision) : DEFAULT_PRECISION
        }
      }
    });
  } // setNodeDefaults()

  // Called when the user selects edit option in tree
  public get getTelemetryNodeName(): string {
    return this.editingNode != undefined ? this.editingNode.displayName : ''
  } // getTelemetryNodeName()

  public changeSettingsRequest() {
    // TODO Just to be safe temp until editMode init done, prevent errors
    if (this.editingNode.settings == undefined) {
      this.editingNode.settings = structuredClone(LIST_SIGNAL_DEFAULT_SETTINGS);
    }

    this.functionFrmCtl.setValue(this.editingNode.settings.function);
    this.functionTimeframeFrmCtrl.setValue(this.editingNode.settings.functionTimeframe)
    this.showSourceNameFrmCtrl.setValue(this.editingNode.settings.showSourceName);
    this.showDeviceNameFrmCtrl.setValue(this.editingNode.settings.showDeviceName);
    this.showUoMSymbolFrmCtrl.setValue(this.editingNode.settings.showUoMSymbol);
    this.showCustomNameFrmCtrl.setValue(this.editingNode.settings.showCustomName);
    this.customNameFrmCtrl.setValue(this.editingNode.settings.customName);
    this.precisionFrmCtrl.setValue(this.editingNode.settings.precision)

    this.showSettingsDrawer = true;
  } // changeSettings()

  public applyDrawerDisabled(): boolean {
    if ((this.functionTimeframeFrmCtrl.value < this.minFunctionTimeframe)
      || (this.functionTimeframeFrmCtrl.value > this.maxFunctionTimeframe)) {
      return true;
    }
    return false;
  } // applyDrawerDisabled()

  public applyDrawer() {
    this.showSettingsDrawer = false;

    // Save the settings
    this.editingNode.settings.function = this.functionFrmCtl.value;
    this.editingNode.settings.functionTimeframe = this.functionTimeframeFrmCtrl.value;
    this.editingNode.settings.showSourceName = this.showSourceNameFrmCtrl.value;
    this.editingNode.settings.showDeviceName = this.showDeviceNameFrmCtrl.value;
    this.editingNode.settings.showUoMSymbol = this.showUoMSymbolFrmCtrl.value;
    this.editingNode.settings.showCustomName = this.showCustomNameFrmCtrl.value;
    this.editingNode.settings.customName = this.customNameFrmCtrl.value;
    this.editingNode.settings.precision = this.precisionFrmCtrl.value;
  } // applyDrawer()

  public cancelDrawer() {
    this.showSettingsDrawer = false;
  } // cancelDrawer()

  public drawerClosed() {
    this.showSettingsDrawer = false;
  }

  public getShowAll(): boolean {
    return this.showAll
  } // getShowAll()

  public typeFilter(): Array<TelemetryType> | undefined {
    return [TelemetryType.Telemetry]
  }

  public deliveryFilter(): Array<TelemetryDeliveryMethod> | undefined {
    return [TelemetryDeliveryMethod.Streaming]
  }

  public getEditMode(): boolean {
    return this.editMode;
  } // getEditMode()

  // Used by Dashboard when it creates a Line Widget
  public static getDefault(): ListSettings {
    return LIST_DEFAULT_SETTINGS;
  } // getDefault()

  // Apply settings selected, update settings
  public apply() {
    this.data.settings.sourceNodes = <Array<any>>SourceSelectionComponent.pruneTree(this.fullTree);
    this.setTelemetryNodeDefaults(this.data.settings.sourceNodes);

    this.data.settings.title.showTitle = this.userForm.get('titleShowFrmCtrl')?.getRawValue()
    this.data.settings.title.text = this.userForm.get('titleTextFrmCtrl')?.getRawValue()
    this.data.settings.title.align = this.userForm.get('titleAlignFrmCtrl')?.getRawValue()
    this.data.settings.title.font.size = this.userForm.get('titleSizeFrmCtrl')?.getRawValue()

    // Apply selected, return selected data
    this.dialogRef.close({
      event: 'apply',
      data: this.data
    });
  } // apply()

  // Cancel selected, discard settings
  public cancel() {
    // If we are loading, then cleanup
    if (this.loadingId) {
      this.spinner.hide(this.spinnerName);
      window.clearInterval(this.loadingId)
      this.loadingId = 0;
    }
    // Issue Close
    this.dialogRef.close({
      event: 'cancel',
      data: undefined
    });
  } // cancel()

  public get spinnerType(): string {
    return SpinnerTypes.BallClipRotate;
  }

  public get spinnerName(): string {
    return "sourceList";
  }

  public get isSourcesListReady(): boolean {
    return DefinitionService.SourcesListReady;
  }

  public get getALIGN(): any {
    return ALIGN;
  }

} // class ListWidgetSettingsComponent
